import {
  Avatar,
  ChatContainer,
  MainContainer,
  Message,
  MessageInput,
  MessageList,
  MessageSeparator,
  TypingIndicator
} from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { SettingsOutlined } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Grid, IconButton, Tooltip, Typography, styled } from '@mui/material';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import remarkGfm from 'remark-gfm';
import { useAuth } from 'services/auth-service';
import AIAssistantService, {
  AIAssistantMessageType,
  ChatBotSessionProps,
  ParsedConversation
} from 'services/hooks/ai-assistant-service';
import {
  AIAssistantMessageDirection,
  AIAssistantMessagePosition,
  AI_CHAT_ASSISTANT,
  CHAT_USER,
  LOCAL_STORAGE_KEYS,
  colours,
  defaultUUIDValue
} from 'utils/constants';
import { generateApiFailErrorMessage } from 'utils/helpers';
import { betaIndicatorStyle } from 'utils/styles';
import './AIAssistant.css';
import CustomSnackbar from './CustomSnackbar';
import FeedbackModal from './FeedbackModal';
import MessageFooter from './MessageFooter';
import { useNavigate } from 'react-router-dom';
import { parseConversation, transformRawMessage } from 'utils/parse';

// ArrowForwardIcon styling
const CustomArrowForwardIcon = styled(ArrowForwardIcon)({
  color: '#34a5ac',
  alignSelf: 'flex-end'
});

/* Define the maximum input length. */
const MAX_INPUT_LENGTH = 4096;

/* Define the default AI assistant message. */
const DEFAULT_AI_ASSISTANT_MSG = 'Hello! How can I assist you today?';

/* Define the default AI assistant error message. */
const DEFAULT_AI_ASSISTANT_ERROR_MSG =
  'We encountered an issue. Please try rephrasing your question, or try again later. If the issue continues, our support team is here to help. You can contact us at support@b-line.io.';

const AIAssistant: React.FC = () => {
  const {
    state: { user }
  } = useAuth();
  const [messages, setMessages] = useState<AIAssistantMessageType[]>([
    {
      message: DEFAULT_AI_ASSISTANT_MSG,
      sender: AI_CHAT_ASSISTANT,
      direction: AIAssistantMessageDirection.Incoming,
      position: AIAssistantMessagePosition.Single
    }
  ]);
  const [typing, setTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const [feedbackUuid, setFeedbackUuid] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; isError: boolean }>({
    open: false,
    message: '',
    isError: false
  });
  const [feedbackState, setFeedbackState] = useState<{
    [key: number]: 'liked' | 'disliked' | null;
  }>({});
  const navigate = useNavigate();
  const hasLoadedInitialMessages = useRef(false);

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: '', isError: false });
  };

  const handleOpenFeedbackModal = () => setOpenFeedbackModal(true);

  const handleCloseFeedbackModal = () => {
    setOpenFeedbackModal(false);
    setFeedbackMessage('');
    setFeedbackUuid(null);
  };

  const { data: chatSessionData, isLoading } = useQuery<ChatBotSessionProps, Error>(
    ['chatSession', user.sub],
    AIAssistantService.getChatBotSession,
    {
      enabled: !!user.sub,
      cacheTime: 0,
      onError: () => {
        toast.error(generateApiFailErrorMessage('User Session'));
      }
    }
  );

  const {} = useQuery<unknown, Error>(
    ['userContext', user.sub],
    () => AIAssistantService.UpdateUserContext(user.sub!),
    {
      enabled: !!user.sub, // Only run the query if user.sub is truthy
      onError: () => {
        toast.error(generateApiFailErrorMessage('User Context'));
      },
      onSuccess: () => {}
    }
  );

  // Use effect to set aiChatSessionId in local storage and parse conversation
  useEffect(() => {
    if (
      !hasLoadedInitialMessages.current &&
      chatSessionData &&
      chatSessionData.session_id !== defaultUUIDValue
    ) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.aiChatSessionId, chatSessionData.session_id);

      const parsedConversation: ParsedConversation | null = parseConversation(
        chatSessionData.conversation
      );

      if (parsedConversation) {
        const chatMessages: AIAssistantMessageType[] = parsedConversation.map((msg) =>
          transformRawMessage(msg, AIAssistantMessagePosition.Normal)
        );

        // Get the last 5 conversations (each conversation consists of user + assistant)
        const lastFiveConversations: AIAssistantMessageType[] = [];
        for (let i = chatMessages.length - 1; i > 0; i -= 2) {
          if (lastFiveConversations.length >= 10) break;
          lastFiveConversations.unshift(chatMessages[i]);
          lastFiveConversations.unshift(chatMessages[i - 1]);
        }

        if (lastFiveConversations.length > 0) {
          setMessages(lastFiveConversations);
          setShowSuggestions(false);
        } else {
          setMessages([
            {
              message: DEFAULT_AI_ASSISTANT_MSG,
              sender: AI_CHAT_ASSISTANT,
              direction: AIAssistantMessageDirection.Incoming,
              position: AIAssistantMessagePosition.Normal
            }
          ]);
          setShowSuggestions(true);
        }
      } else {
        // If parsing fails, fallback to default error message
        setMessages([
          {
            message: DEFAULT_AI_ASSISTANT_MSG,
            sender: AI_CHAT_ASSISTANT,
            direction: AIAssistantMessageDirection.Incoming,
            position: AIAssistantMessagePosition.Normal
          }
        ]);
        setShowSuggestions(true);
      }
      hasLoadedInitialMessages.current = true;
    }
  }, [chatSessionData]);

  const handleSendWithAPI = async (message: string) => {
    if (!user.sub) {
      console.error('Cannot send AI message without user uuid');
      return;
    }

    setInputValue(''); // Clear input after sending
    if (message.trim()) {
      const userMessage: AIAssistantMessageType = {
        message,
        sender: CHAT_USER,
        direction: AIAssistantMessageDirection.Outgoing,
        position: AIAssistantMessagePosition.Normal
      };
      setMessages((prevMessages) => [...prevMessages, userMessage]);
      setTyping(true);
      setShowSuggestions(false);

      // streaming callback handler
      const handleStreamedMessage = (newMessage: string) => {
        setMessages((prevMessages) => {
          const lastMessage = prevMessages[prevMessages.length - 1];

          if (lastMessage.sender === AI_CHAT_ASSISTANT) {
            // Update the last AI message progressively
            return [...prevMessages.slice(0, -1), { ...lastMessage, message: newMessage }];
          }

          // Append a new AI message if it's the first one
          const botMessage: AIAssistantMessageType = {
            message: newMessage,
            sender: AI_CHAT_ASSISTANT,
            direction: AIAssistantMessageDirection.Incoming,
            position: AIAssistantMessagePosition.Normal
          };
          return [...prevMessages, botMessage];
        });
      };

      // Send the request and handle the streaming response
      await AIAssistantService.fetchAIResponse(
        { session_id: chatSessionData?.session_id, roleUuid: user.sub, prompt: message },
        handleStreamedMessage
      );
      setTyping(false);
    }
  };

  const handleInputChange = (innerHtml: string, textContent: string) => {
    const sanitizedInput = DOMPurify.sanitize(innerHtml);
    if (textContent.length <= MAX_INPUT_LENGTH) {
      setInputValue(sanitizedInput);
    }
  };

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const selection = window.getSelection();

    if (selection) {
      const range = selection.getRangeAt(0);

      // Get the editor reference
      const editor = document.querySelector('.cs-message-input__content-editor') as HTMLDivElement;

      // Get the current content length and selected text length
      const currentContentLength = editor?.textContent?.length || 0;
      const selectedTextLength = range.toString().length;

      // Clean the html by getting only the text content from the pasted data
      let text = e.clipboardData.getData('text/plain');

      // Calculate the available length by considering the selected text
      const availableLength = MAX_INPUT_LENGTH - currentContentLength + selectedTextLength;
      if (text.length > availableLength) {
        text = text.substring(0, availableLength);
      }

      // If something is selected, delete it
      range.deleteContents();

      // Paste clean text at the cursor position
      range.insertNode(document.createTextNode(text));

      // Move cursor to the end of the pasted text
      selection.collapseToEnd();

      if (editor) {
        // The value was manually inserted into the editor without changing the state to avoid cursor placement issues and
        // keep the send button disabled if the input was empty.
        // Therefore, the "input" event is dispatched manually to trigger the onChange event and update the state.
        editor.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (typing || !inputValue)) {
      event.preventDefault();
    }
  };

  const handleSampleQuestionClick = (question: string) => {
    handleSendWithAPI(question);
  };

  const sampleQuestions = [
    'Tell me what days of the year are the busiest for each of my buildings.',
    'Please provide the list of all spaces under my buildings with their capacities.',
    "What's the difference between a visitor and an occupant?"
  ];

  if (isLoading) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      bgcolor={colours.lightGrey}
      className="parent-chat-container"
    >
      <Box width="100%" maxWidth="800px" className="main-container">
        <Grid container justifyContent="space-between">
          <Grid item xs={11}>
            <Typography variant="h4" component="h1">
              AI Assistant
              <span style={betaIndicatorStyle}>
                <Tooltip title={'Currently in Beta - Please provide feedback'}>
                  <span>BETA</span>
                </Tooltip>
              </span>
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Tooltip title="Building Documentation for AI Assistant">
              <IconButton
                sx={{ color: 'black' }}
                onClick={() => {
                  navigate('/ai-assistant/settings');
                }}
              >
                <SettingsOutlined />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <MainContainer className="main-container hide-scrollbar">
          <ChatContainer className="main-container hide-scrollbar">
            <MessageList
              typingIndicator={typing && <TypingIndicator content="AI Assistant is typing" />}
              className="message-list-container"
            >
              {messages
                .filter((msg) => {
                  return msg && msg.message;
                })
                .map((msg, index) => (
                  <Message
                    key={index}
                    model={{
                      sender: msg.sender,
                      direction: msg.direction,
                      position: AIAssistantMessagePosition.Normal,
                      type: 'custom'
                    }}
                    style={{ width: '100%' }}
                    avatarPosition="tl"
                    avatarSpacer
                    className={`chat-message ${
                      msg.direction === AIAssistantMessageDirection.Outgoing
                        ? CHAT_USER
                        : AI_CHAT_ASSISTANT
                    }`}
                  >
                    {msg.sender === AI_CHAT_ASSISTANT && <Avatar size="sm" src="/favicon.ico" />}
                    <Message.CustomContent className="markdown-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.message}</ReactMarkdown>
                    </Message.CustomContent>
                    {msg.sender === AI_CHAT_ASSISTANT &&
                      msg.message !== DEFAULT_AI_ASSISTANT_MSG &&
                      msg.message !== DEFAULT_AI_ASSISTANT_ERROR_MSG && (
                        <Message.Footer className="message-footer">
                          {/* Message Footer */}
                          <MessageFooter
                            message={msg}
                            index={index}
                            messages={messages}
                            feedbackState={feedbackState}
                            handleThumbsUp={(msgIndex: number) => {
                              setFeedbackState((prevState) => ({
                                ...prevState,
                                [msgIndex]: 'liked'
                              }));
                            }}
                            handleThumbsDown={(msgIndex: number) => {
                              setFeedbackState((prevState) => ({
                                ...prevState,
                                [msgIndex]: 'disliked'
                              }));
                            }}
                            setSnackbar={setSnackbar}
                            setFeedbackUuid={setFeedbackUuid}
                            handleOpenFeedbackModal={handleOpenFeedbackModal}
                          />
                        </Message.Footer>
                      )}
                  </Message>
                ))}
              {showSuggestions && (
                <MessageSeparator>
                  <Box p={2} className="suggestions-container">
                    <div className="suggestions-grid">
                      {sampleQuestions.map((question, index) => (
                        <div className="suggestions-grid-item" key={index}>
                          {question && (
                            <Button
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                border: '1px solid #ADC3C9',
                                borderRadius: '16px',
                                backgroundColor: '#ffffff',
                                height: '100%',
                                color: 'black',
                                textTransform: 'none',
                                '& .MuiButton-label': {
                                  textTransform: 'lowercase'
                                },
                                padding: '15px',
                                '&:hover': {
                                  borderColor: '#ADC3C9'
                                }
                              }}
                              variant="outlined"
                              fullWidth
                              onClick={() => handleSampleQuestionClick(question)}
                            >
                              <span style={{ flexGrow: 1, textAlign: 'left' }}>{question}</span>
                              <CustomArrowForwardIcon />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </Box>
                </MessageSeparator>
              )}
            </MessageList>
            <MessageInput
              placeholder="Ask us anything about your buildings"
              value={inputValue}
              onChange={handleInputChange}
              onSend={handleSendWithAPI}
              attachButton={false}
              className="message-input-container"
              sendDisabled={typing || !inputValue}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
            />
          </ChatContainer>
        </MainContainer>
      </Box>

      {/* Feedback Modal */}
      <FeedbackModal
        openFeedbackModal={openFeedbackModal}
        handleCloseFeedbackModal={handleCloseFeedbackModal}
        feedbackMessage={feedbackMessage}
        setFeedbackMessage={setFeedbackMessage}
        feedbackUuid={feedbackUuid}
        setSnackbar={setSnackbar}
      />

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        onClose={handleSnackbarClose}
        isError={snackbar.isError}
      />
    </Box>
  );
};

export default AIAssistant;
